import { useEffect, useState } from "react";
import { api } from "./api.ts";
import { useMotyw, zsynchronizujAkcent } from "./motyw.ts";
import { useStore } from "./store.ts";
import { useBlokadaWygaszania } from "./wake.ts";
import { Ekran, Komunikat, Przycisk } from "./ui.tsx";
import { Login } from "./screens/Login.tsx";
import { Invite } from "./screens/Invite.tsx";
import { Home } from "./screens/Home.tsx";
import { Game } from "./screens/Game.tsx";

/**
 * Wybór ekranu.
 *
 * Bez routera: aplikacja ma cztery stany, a jedyny adres, który musi działać
 * z zewnątrz, to link zaproszeniowy `/z/<token>` wklejony z WhatsAppa.
 * Biblioteka routingu kosztowałaby więcej kilobajtów niż wnosi.
 */
export function App() {
  const { user, view, setUser, connect, outdated, toast, showToast } = useStore();
  const [gotowe, setGotowe] = useState(false);
  const [token, setToken] = useState<string | null>(() => odczytajToken());

  // Dla efektu ubocznego: skrypt w `<head>` ustawia motyw raz, ten hook
  // utrzymuje go w zgodzie z systemem przez resztę życia aplikacji.
  useMotyw();

  // Ekran nie gaśnie w trakcie partii — także w zainstalowanej aplikacji,
  // gdzie nie ma paska przeglądarki, o który dałoby się zaczepić palcem.
  useBlokadaWygaszania(Boolean(view));

  // Kim jestem — pierwszy strzał po załadowaniu. Sesja żyje rok, więc zwykle
  // od razu wchodzimy do gry.
  useEffect(() => {
    api
      .me()
      .then(({ user: me }) => {
        setUser(me);
        // Konto jest źródłem prawdy; cache w localStorage tylko pomalował ekran
        // przed pierwszym renderem i mógł być z innego urządzenia.
        zsynchronizujAkcent(me.kolor);
      })
      .catch(() => setUser(null))
      .finally(() => setGotowe(true));
  }, [setUser]);

  useEffect(() => {
    if (user) connect();
  }, [user, connect]);

  // Powiadomienia znikają same — w trakcie rundy nie ma czasu ich zamykać.
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => showToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast, showToast]);

  if (outdated) return <Nieaktualny />;
  if (!gotowe) return <Ladowanie />;

  if (token) {
    return (
      <Invite
        token={token}
        onDone={() => {
          window.history.replaceState({}, "", "/");
          setToken(null);
        }}
      />
    );
  }

  if (!user) return <Login />;

  return (
    <>
      {view ? <Game view={view} /> : <Home />}
      {toast ? (
        <div className="fixed left-4 right-4 bottom-24 bg-panel-jasny border border-obrys rounded-2xl px-4 py-3 text-center shadow-lg">
          {toast}
        </div>
      ) : null}
    </>
  );
}

/** Link zaproszeniowy w postaci `/z/<token>`. */
function odczytajToken(): string | null {
  const match = /^\/z\/([^/?#]+)/.exec(window.location.pathname);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

function Ladowanie() {
  return (
    <Ekran tytul="Gierki">
      <Komunikat>Chwileczkę…</Komunikat>
    </Ekran>
  );
}

/**
 * Klient z nieaktualną wersją protokołu.
 *
 * Bez tego ekranu telefon z zakeszowanym service workerem po deployu wygląda
 * na zepsuty tylko u jednej osoby — najgorszy rodzaj błędu do zdiagnozowania
 * w jadącym samochodzie.
 */
function Nieaktualny() {
  const odswiez = async () => {
    try {
      const registrations = await navigator.serviceWorker?.getRegistrations?.();
      await Promise.all((registrations ?? []).map((registration) => registration.unregister()));
      const klucze = await caches?.keys?.();
      await Promise.all((klucze ?? []).map((klucz) => caches.delete(klucz)));
    } catch {
      // Nawet jeśli czyszczenie się nie uda, przeładowanie zwykle wystarcza.
    }
    window.location.reload();
  };

  return (
    <Ekran tytul="Nowa wersja">
      <Komunikat>
        Ta wersja gry jest już nieaktualna. Odśwież, żeby grać dalej — Twoja partia czeka.
      </Komunikat>
      <Przycisk wariant="glowny" pelna onClick={() => void odswiez()}>
        Odśwież
      </Przycisk>
    </Ekran>
  );
}
